import JsPDF from 'jspdf';
import moment from 'moment';
import 'moment/locale/fr';

import Logo from 'assets/img/Logo_EPS_300.png';
import star0 from 'assets/img/Fichier 1.png';
import star1 from 'assets/img/Fichier 2.png';
import star2 from 'assets/img/Fichier 3.png';
import star3 from 'assets/img/Fichier 4.png';
import star4 from 'assets/img/Fichier 5.png';

const STUDENTS_PER_PAGE = 17;

const COLORS = {
    ink: [17, 28, 67],
    text: [38, 52, 81],
    muted: [122, 135, 159],
    blue: [42, 97, 213],
    violet: [117, 80, 216],
    violetSoft: [240, 235, 255],
    goldSoft: [255, 240, 194],
    border: [220, 227, 238],
    line: [229, 234, 242],
    panel: [251, 252, 255],
    head: [244, 247, 253],
    white: [255, 255, 255],
};

let assetPromise;

function loadImage(source) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = source;
    });
}

function loadAssets() {
    if (!assetPromise) assetPromise = Promise.all([Logo, star0, star1, star2, star3, star4].map(loadImage));
    return assetPromise;
}

function chunkRows(rows, size) {
    const chunks = [];
    for (let index = 0; index < rows.length; index += size) chunks.push(rows.slice(index, index + size));
    return chunks.length > 0 ? chunks : [[]];
}

function setText(doc, color, size, weight = 'normal') {
    doc.setTextColor(...color);
    doc.setFont('helvetica', weight);
    doc.setFontSize(size);
}

function truncate(doc, value, maxWidth) {
    const text = String(value || '—');
    if (doc.getTextWidth(text) <= maxWidth) return text;

    let shortened = text;
    while (shortened.length > 1 && doc.getTextWidth(`${shortened}…`) > maxWidth) shortened = shortened.slice(0, -1);
    return `${shortened}…`;
}

function drawRoundedPanel(doc, x, y, width, height, fill = COLORS.panel, radius = 7) {
    doc.setFillColor(...fill);
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.8);
    doc.roundedRect(x, y, width, height, radius, radius, 'FD');
}

function drawSummaryCard(doc, x, y, width, label, value) {
    drawRoundedPanel(doc, x, y, width, 52);
    setText(doc, COLORS.muted, 7, 'bold');
    doc.text(label, x + 11, y + 17);
    setText(doc, COLORS.ink, 9.5, 'bold');
    doc.text(truncate(doc, value, width - 22), x + 11, y + 37);
}

function getEvaluationAsset(note, assets) {
    if (Number(note) === 4) return assets[1];
    if (Number(note) === 3) return assets[2];
    if (Number(note) === 2) return assets[3];
    if (Number(note) === 1) return assets[4];
    return assets[5];
}

function drawDecorations(doc) {
    doc.setDrawColor(232, 229, 255);
    doc.setLineWidth(24);
    doc.circle(826, 7, 48, 'S');
    doc.setDrawColor(255, 227, 157);
    doc.setLineWidth(22);
    doc.circle(816, 594, 45, 'S');
    doc.setLineWidth(0.2);
}

function drawHeader(doc, assets) {
    doc.addImage(assets[0], 'PNG', 35, 24, 45, 40.5);
    setText(doc, COLORS.ink, 14, 'bold');
    doc.text('ACCÈS EPS', 91, 45);
    setText(doc, COLORS.muted, 7.5);
    doc.text('Synthèse pédagogique', 91, 59);

    doc.setFillColor(244, 247, 255);
    doc.setDrawColor(213, 225, 250);
    doc.roundedRect(653, 31, 147, 25, 12.5, 12.5, 'FD');
    setText(doc, COLORS.blue, 8, 'bold');
    doc.text('SUIVI DES ÉVALUATIONS', 726.5, 47, {align: 'center'});

    doc.setDrawColor(...COLORS.line);
    doc.setLineWidth(0.8);
    doc.line(35, 77, 807, 77);
}

function drawContext(doc, context, reportData, activityPageIndex, activityPageCount) {
    setText(doc, COLORS.blue, 8, 'bold');
    doc.text('BILAN PÉDAGOGIQUE', 35, 101);
    setText(doc, COLORS.ink, 23, 'bold');
    doc.text('Suivi des évaluations', 35, 128);
    setText(doc, COLORS.muted, 9);
    doc.text('Synthèse des acquis de la classe pour les activités sélectionnées.', 35, 144);

    doc.setFillColor(...COLORS.violetSoft);
    doc.roundedRect(700, 112, 100, 25, 12.5, 12.5, 'F');
    setText(doc, COLORS.violet, 8, 'bold');
    doc.text(`Export ${activityPageIndex + 1} / ${activityPageCount}`, 750, 128, {align: 'center'});

    const x = 35;
    const y = 158;
    const gap = 8;
    const widths = [184, 93, 153, 153, 157];
    const labels = ['Classe et niveau(x)', 'Cycle', 'Compétence', 'Séquence', 'Catégorie'];
    const values = [
        `${reportData.classInfo.nom_classe || `Classe ${context.idClasse}`} · ${reportData.classInfo.niveau || '—'}`,
        `Cycle ${context.cycleSelected}`,
        context.competenceSelected?.nom || '—',
        context.sequenceSelected?.nom || '—',
        context.categorieSelected?.nom || 'Toutes les activités',
    ];

    let cursor = x;
    widths.forEach((width, index) => {
        drawSummaryCard(doc, cursor, y, width, labels[index], values[index]);
        cursor += width + gap;
    });
}

function drawTable(doc, activities, students, reportData, assets) {
    const x = 35;
    const y = 226;
    const width = 772;
    const titleHeight = 29;
    const headerHeight = 42;
    const rowHeight = 15;
    const studentWidth = 155;
    const activityWidth = (width - studentWidth) / activities.length;
    const tableHeight = titleHeight + headerHeight + (students.length * rowHeight);

    doc.setFillColor(...COLORS.white);
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.8);
    doc.roundedRect(x, y, width, tableHeight, 7, 7, 'FD');

    setText(doc, COLORS.ink, 11.5, 'bold');
    doc.text('Résultats de la classe', x + 13, y + 19);
    setText(doc, COLORS.muted, 7.5);
    doc.text(`${reportData.students.length} élève${reportData.students.length > 1 ? 's' : ''} · ${activities.length} activité${activities.length > 1 ? 's' : ''}`, x + width - 13, y + 19, {align: 'right'});

    const headerY = y + titleHeight;
    doc.setFillColor(...COLORS.head);
    doc.rect(x, headerY, width, headerHeight, 'F');
    doc.setDrawColor(...COLORS.border);
    doc.line(x, headerY, x + width, headerY);
    doc.line(x, headerY + headerHeight, x + width, headerY + headerHeight);
    doc.line(x + studentWidth, headerY, x + studentWidth, y + tableHeight);

    setText(doc, COLORS.blue, 7.5, 'bold');
    doc.text('Élève', x + 13, headerY + 24);

    activities.forEach((activity, index) => {
        const cellX = x + studentWidth + (index * activityWidth);
        if (index > 0) doc.line(cellX, headerY, cellX, y + tableHeight);
        const title = activity.objectif || activity.titre_activite || activity.titre || `Activité ${index + 1}`;
        const lines = doc.splitTextToSize(title, activityWidth - 12).slice(0, 3);
        const lineHeight = 8;
        const firstLineY = headerY + ((headerHeight - (lines.length * lineHeight)) / 2) + 6;
        doc.text(lines, cellX + (activityWidth / 2), firstLineY, {align: 'center', lineHeightFactor: 1.05});
    });

    const notesBySession = new Map(reportData.notesBySession.map((entry) => [String(entry.id), entry.notes]));
    students.forEach((student, studentIndex) => {
        const rowY = headerY + headerHeight + (studentIndex * rowHeight);
        if (studentIndex % 2 === 1) {
            doc.setFillColor(...COLORS.panel);
            doc.rect(x, rowY, width, rowHeight, 'F');
        }
        doc.setDrawColor(...COLORS.line);
        doc.line(x, rowY + rowHeight, x + width, rowY + rowHeight);

        setText(doc, COLORS.text, 7.2, 'bold');
        doc.text(truncate(doc, `${student.prenom} ${student.nom}`, studentWidth - 20), x + 13, rowY + 10.2);

        activities.forEach((activity, activityIndex) => {
            const sessionNotes = notesBySession.get(String(activity.id)) || [];
            const evaluation = sessionNotes.find((note) => String(note.id_eleve) === String(student.id_eleve));
            const image = getEvaluationAsset(evaluation?.note, assets);
            const centerX = x + studentWidth + (activityIndex * activityWidth) + (activityWidth / 2);
            doc.addImage(image, 'PNG', centerX - 12, rowY + 5.1, 24, 4.8);
        });
    });
}

function drawFooter(doc, pageIndex, pageCount) {
    doc.setDrawColor(...COLORS.line);
    doc.line(35, 571, 807, 571);
    setText(doc, COLORS.muted, 6.8);
    doc.text("© 2024 Accès EPS — Document généré depuis l'espace enseignant", 35, 585);
    doc.text(`Édité le ${moment().format('DD/MM/YYYY')} · Page ${pageIndex + 1} / ${pageCount}`, 807, 585, {align: 'right'});
}

export async function generateEvaluationPdf({
    idClasse,
    reportData,
    activityPageIndex,
    cycleSelected,
    competenceSelected,
    sequenceSelected,
    categorieSelected,
}) {
    const assets = await loadAssets();
    const activities = reportData.activityPages[activityPageIndex];
    if (!activities || activities.length === 0) throw new Error('No activities to export');

    const studentPages = chunkRows(reportData.students, STUDENTS_PER_PAGE);
    const doc = new JsPDF('l', 'pt', 'a4');
    const context = {idClasse, cycleSelected, competenceSelected, sequenceSelected, categorieSelected};

    studentPages.forEach((students, pageIndex) => {
        if (pageIndex > 0) doc.addPage('a4', 'landscape');
        drawDecorations(doc);
        drawHeader(doc, assets);
        drawContext(doc, context, reportData, activityPageIndex, reportData.activityPages.length);
        drawTable(doc, activities, students, reportData, assets);
        drawFooter(doc, pageIndex, studentPages.length);
    });

    doc.save(`suivi-evaluations-classe-${idClasse}-${activityPageIndex + 1}.pdf`);
}
